import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  Chip,
  TablePagination,
  Alert,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  Update,
  UploadFile,
  Description,
  Business,
  Receipt,
  Person,
  AttachMoney,
  CheckCircle,
  Warning,
  Info,
  Home,
  LocationOn,
} from '@mui/icons-material';

const PlotRegistry = () => {
  const canWriteRegistry = hasWritePermission('plot-booking.plot-registry');
  const [searchForm, setSearchForm] = useState({
    bookingId: '',
  });

  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [tabValue, setTabValue] = useState(0);

  // Agreement form state
  const [agreementForm, setAgreementForm] = useState({
    agreementDone: false,
    agreementDate: '',
    agreementReceivedDate: '',
    agreementReceivedName: '',
    scanAgreementCopy: null,
  });

  // Registry form state
  const [registryForm, setRegistryForm] = useState({
    registryDone: false,
    registryDate: '',
    registryReceivedDate: '',
    registryReceivedName: '',
    scanRegistryCopy: null,
    stampDutyFee: '',
  });

  // Sample booking data
  const [bookingData] = useState([
    {
      id: 1,
      bookingId: 'BK001',
      loginId: 'LG001',
      customerName: 'John Smith',
      plotAmount: '₹54,00,000',
      paidAmount: '₹5,40,000',
      bookingDate: '2025-01-15',
      plotNo: 'P101',
      registryStatus: 'Pending',
      agreementStatus: 'Completed',
      siteName: 'Green Valley',
      sectorName: 'Sector A',
      blockName: 'Block 1',
      plotSize: '30x40 ft',
      plotArea: '1200 sq ft',
      mobileNo: '9876543210',
      balanceAmount: '₹48,60,000',
    },
    {
      id: 2,
      bookingId: 'BK002',
      loginId: 'LG002',
      customerName: 'Sarah Johnson',
      plotAmount: '₹81,90,000',
      paidAmount: '₹20,47,500',
      bookingDate: '2025-02-20',
      plotNo: 'P205',
      registryStatus: 'In Progress',
      agreementStatus: 'Pending',
      siteName: 'Hill View',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      plotSize: '35x45 ft',
      plotArea: '1575 sq ft',
      mobileNo: '9876543211',
      balanceAmount: '₹61,42,500',
    },
    {
      id: 3,
      bookingId: 'BK003',
      loginId: 'LG003',
      customerName: 'Michael Brown',
      plotAmount: '₹1,20,00,000',
      paidAmount: '₹12,00,000',
      bookingDate: '2025-03-10',
      plotNo: 'P321',
      registryStatus: 'Completed',
      agreementStatus: 'Completed',
      siteName: 'Paradise Heights',
      sectorName: 'Sector C',
      blockName: 'Block 3',
      plotSize: '40x50 ft',
      plotArea: '2000 sq ft',
      mobileNo: '9876543212',
      balanceAmount: '₹1,08,00,000',
    },
    {
      id: 4,
      bookingId: 'BK004',
      loginId: 'LG004',
      customerName: 'Emily Davis',
      plotAmount: '₹1,85,62,500',
      paidAmount: '₹37,12,500',
      bookingDate: '2025-04-05',
      plotNo: 'P456',
      registryStatus: 'Pending',
      agreementStatus: 'In Progress',
      siteName: 'Royal Gardens',
      sectorName: 'Sector D',
      blockName: 'Block 4',
      plotSize: '45x55 ft',
      plotArea: '2475 sq ft',
      mobileNo: '9876543213',
      balanceAmount: '₹1,48,50,000',
    },
  ]);

  const handleInputChange = (field, value) => {
    setSearchForm({
      ...searchForm,
      [field]: value,
    });
  };

  const handleSearch = async () => {
    if (!searchForm.bookingId.trim()) {
      setErrors({ bookingId: 'Please enter a Booking ID' });
      return;
    }

    setSearchLoading(true);
    setErrors({});

    // Simulate API call
    setTimeout(() => {
      setShowBookingDetails(true);
      setPage(0);
      setSuccessMessage('');
      console.log('Searching for booking:', searchForm.bookingId);
      setSearchLoading(false);
    }, 1500);
  };

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleAgreementFormChange = (field, value) => {
    setAgreementForm({
      ...agreementForm,
      [field]: value,
    });
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleRegistryFormChange = (field, value) => {
    setRegistryForm({
      ...registryForm,
      [field]: value,
    });
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleFileUpload = (field, event) => {
    const file = event.target.files[0];
    if (field === 'scanAgreementCopy') {
      setAgreementForm({
        ...agreementForm,
        [field]: file,
      });
    } else if (field === 'scanRegistryCopy') {
      setRegistryForm({
        ...registryForm,
        [field]: file,
      });
    }
  };

  const handleUpdateStatus = async () => {
    setSubmitLoading(true);

    // Simulate API call
    setTimeout(() => {
      setSuccessMessage('Plot registry and agreement status updated successfully!');
      console.log('Agreement Form:', agreementForm);
      console.log('Registry Form:', registryForm);
      setSubmitLoading(false);
    }, 2000);
  };

  // Calculate statistics
  const totalBookings = bookingData.length;
  const completedRegistry = bookingData.filter(booking => booking.registryStatus === 'Completed').length;
  const pendingRegistry = bookingData.filter(booking => booking.registryStatus === 'Pending').length;
  const inProgressRegistry = bookingData.filter(booking => booking.registryStatus === 'In Progress').length;
  const completedAgreement = bookingData.filter(booking => booking.agreementStatus === 'Completed').length;

  const getFilteredByStatus = (status) => {
    return getFilteredBookingData().filter(booking => !status || booking.registryStatus === status);
  };

  const getFilteredBookingData = () => {
    if (!showBookingDetails || !searchForm.bookingId.trim()) return [];

    return bookingData.filter(booking =>
      booking.bookingId.toLowerCase().includes(searchForm.bookingId.toLowerCase())
    );
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Pending':
        return {
          backgroundColor: '#ff9800',
          color: 'white',
        };
      case 'In Progress':
        return {
          backgroundColor: '#2196f3',
          color: 'white',
        };
      case 'Completed':
        return {
          backgroundColor: '#4caf50',
          color: 'white',
        };
      default:
        return {};
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Plot Registry Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage plot registry and agreement status for bookings
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Receipt sx={{ color: '#1976d2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Bookings
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {totalBookings}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <CheckCircle sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Completed Registry
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {completedRegistry}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#fff3e0', 
            border: '1px solid #ffcc02',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Warning sx={{ color: '#f57c00', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Pending Registry
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {pendingRegistry}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e1f5fe', 
            border: '1px solid #81d4fa',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Info sx={{ color: '#0288d1', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  In Progress
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0288d1', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {inProgressRegistry}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#f3e5f5', 
            border: '1px solid #ce93d8',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Description sx={{ color: '#7b1fa2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Completed Agreement
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {completedAgreement}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid> */}

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Search sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search Booking for Registry
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Booking ID"
                variant="outlined"
                fullWidth
                value={searchForm.bookingId}
                onChange={(e) => handleInputChange('bookingId', e.target.value)}
                placeholder="Enter Booking ID (e.g., BK001)"
                error={!!errors.bookingId}
                helperText={errors.bookingId || "Enter the booking ID to search for registry details"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Box>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              height: '56px', // Match TextField height
              mt: 1 // Small margin to align with input area
            }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={searchLoading || !searchForm.bookingId.trim()}
                startIcon={<Search />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2,
                  height: '40px',
                  minHeight: '40px',
                  minWidth: '160px'
                }}
              >
                {searchLoading ? 'Searching...' : 'Search Booking'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      {showBookingDetails && (
        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ px: 3 }}>
              <Tab label={`All Bookings (${getFilteredBookingData().length})`} />
              <Tab label={`Completed Registry (${getFilteredByStatus('Completed').length})`} />
              <Tab label={`Pending Registry (${getFilteredByStatus('Pending').length})`} />
              <Tab label={`In Progress (${getFilteredByStatus('In Progress').length})`} />
            </Tabs>
          </Box>
        </Card>
      )}

      {/* Booking Details Table */}
      {showBookingDetails && (
        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Business sx={{ color: '#1976d2', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Booking Details
                </Typography>
              </Box>

              <Typography variant="body2" color="primary" sx={{ mt: 2, fontWeight: 600 }}>
                Found {getFilteredBookingData().length} booking(s) matching Booking ID: "{searchForm.bookingId}"
              </Typography>
            </Box>

            {getFilteredBookingData().length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Booking Info</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Customer Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Plot Information</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Financial Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Registry Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Agreement Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredBookingData()
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((booking, index) => (
                        <TableRow key={booking.id} hover>
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {booking.bookingId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.loginId} • {booking.bookingDate}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {booking.customerName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.mobileNo}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {booking.plotNo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {booking.siteName} • {booking.sectorName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {booking.plotSize} ({booking.plotArea})
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                {booking.plotAmount}
                              </Typography>
                              <Typography variant="caption" color="primary.main" display="block">
                                Paid: {booking.paidAmount}
                              </Typography>
                              <Typography variant="caption" color="error.main" display="block">
                                Balance: {booking.balanceAmount}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={booking.registryStatus}
                              sx={getStatusStyle(booking.registryStatus)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={booking.agreementStatus}
                              sx={getStatusStyle(booking.agreementStatus)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Alert severity="warning" sx={{ mx: 3, mb: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    No Booking Found
                  </Typography>
                  <Typography variant="body2">
                    No booking found with Booking ID: "{searchForm.bookingId}"
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Please check the booking ID and try again.
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Pagination */}
            <TablePagination
              
              component="div"
              count={getFilteredBookingData().length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ px: 3, py: 2 }}
            />
          </CardContent>
        </Card>
      )}

      {/* Registry and Agreement Forms */}
      {showBookingDetails && getFilteredBookingData().length > 0 && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={4}>
              <Update sx={{ color: '#1976d2', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                Registry & Agreement Management
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {/* Left Side - Plot Agreement Form */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Description sx={{ color: '#1976d2', fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      Plot Agreement
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={agreementForm.agreementDone}
                            onChange={(e) => handleAgreementFormChange('agreementDone', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Agreement Done"
                        sx={{ fontWeight: 600 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Agreement Date"
                        type="date"
                        variant="outlined"
                        fullWidth
                        value={agreementForm.agreementDate}
                        onChange={(e) => handleAgreementFormChange('agreementDate', e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Agreement Received Date"
                        type="date"
                        variant="outlined"
                        fullWidth
                        value={agreementForm.agreementReceivedDate}
                        onChange={(e) => handleAgreementFormChange('agreementReceivedDate', e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Agreement Received Name"
                        variant="outlined"
                        fullWidth
                        value={agreementForm.agreementReceivedName}
                        onChange={(e) => handleAgreementFormChange('agreementReceivedName', e.target.value)}
                        placeholder="Enter name of person who received agreement"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                          Scan Agreement Copy
                        </Typography>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          startIcon={<UploadFile />}
                          sx={{
                            py: 1.5,
                            mb: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2
                          }}
                        >
                          Upload Agreement Copy
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => handleFileUpload('scanAgreementCopy', e)}
                          />
                        </Button>
                        {agreementForm.scanAgreementCopy && (
                          <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                            File selected: {agreementForm.scanAgreementCopy.name}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* Right Side - Plot Registry Form */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Business sx={{ color: '#2e7d32', fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      Plot Registry
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={registryForm.registryDone}
                            onChange={(e) => handleRegistryFormChange('registryDone', e.target.checked)}
                            color="secondary"
                          />
                        }
                        label="Registry Done"
                        sx={{ fontWeight: 600 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Registry Date"
                        type="date"
                        variant="outlined"
                        fullWidth
                        value={registryForm.registryDate}
                        onChange={(e) => handleRegistryFormChange('registryDate', e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Registry Received Date"
                        type="date"
                        variant="outlined"
                        fullWidth
                        value={registryForm.registryReceivedDate}
                        onChange={(e) => handleRegistryFormChange('registryReceivedDate', e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Registry Received Name"
                        variant="outlined"
                        fullWidth
                        value={registryForm.registryReceivedName}
                        onChange={(e) => handleRegistryFormChange('registryReceivedName', e.target.value)}
                        placeholder="Enter name of person who received registry"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                          Scan Registry Copy
                        </Typography>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          startIcon={<UploadFile />}
                          sx={{
                            py: 1.5,
                            mb: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2
                          }}
                        >
                          Upload Registry Copy
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => handleFileUpload('scanRegistryCopy', e)}
                          />
                        </Button>
                        {registryForm.scanRegistryCopy && (
                          <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                            File selected: {registryForm.scanRegistryCopy.name}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Stamp Duty Fee"
                        variant="outlined"
                        fullWidth
                        value={registryForm.stampDutyFee}
                        onChange={(e) => handleRegistryFormChange('stampDutyFee', e.target.value)}
                        placeholder="Enter stamp duty fee amount"
                        helperText="Enter the stamp duty fee in rupees"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoney color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>

            {/* Update Status Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              {/* {canWriteRegistry && ( */}
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleUpdateStatus}
                disabled={submitLoading}
                startIcon={<Update />}
                sx={{
                  px: 6,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  borderRadius: 2
                }}
              >
                {submitLoading ? 'Updating...' : 'Update Registry & Agreement Status'}
              </Button>
              {/* )} */}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PlotRegistry;